(() => {
  class BiolumaCoreCarousel extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.track = this.querySelector('[data-core-track]');
      this.pillsWrap = this.querySelector('[data-core-pills]');
      if (!this.track) return;

      this.dragging = false;
      this.moved = false;
      this.slides = Array.from(this.track.querySelectorAll('.bioluma-core__banner'));
      this.pills = [];
      this.activeIndex = 0;

      this.onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.moved = false;
        this.startX = event.clientX;
        this.scrollStart = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      };

      this.onPointerMove = (event) => {
        if (!this.dragging) return;
        const delta = event.clientX - this.startX;
        if (Math.abs(delta) > 4) this.moved = true;
        this.track.scrollLeft = this.scrollStart - delta;
      };

      this.onPointerUp = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
      };

      this.onClickCapture = (event) => {
        if (!this.moved) return;
        const link = event.target.closest('a');
        if (!link) return;
        event.preventDefault();
        event.stopPropagation();
      };

      this.onScroll = () => {
        window.cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = window.requestAnimationFrame(() => this.updateActivePill());
      };

      this.onResize = () => {
        window.cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = window.requestAnimationFrame(() => this.buildPills());
      };

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.buildPills();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClickCapture, true);
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.scrollRaf);
      window.cancelAnimationFrame(this.resizeRaf);
      this.bound = false;
    }

    getVisibleCount() {
      const root = this.closest('.bioluma-core');
      const styles = root ? getComputedStyle(root) : null;
      const visible = styles ? parseFloat(styles.getPropertyValue('--bc-visible')) : 1;
      return Number.isFinite(visible) ? visible : 1;
    }

    needsPills() {
      this.slides = Array.from(this.track.querySelectorAll('.bioluma-core__banner'));
      if (this.slides.length <= 1) return false;
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    buildPills() {
      if (!this.pillsWrap) return;

      this.slides = Array.from(this.track.querySelectorAll('.bioluma-core__banner'));
      this.pillsWrap.innerHTML = '';
      this.pills = [];

      if (!this.needsPills()) {
        this.pillsWrap.hidden = true;
        return;
      }

      this.pillsWrap.hidden = false;

      this.slides.forEach((slide, index) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = `bioluma-core__pill${index === this.activeIndex ? ' is-active' : ''}`;
        pill.setAttribute('role', 'tab');
        pill.setAttribute('aria-label', `Slide ${index + 1} de ${this.slides.length}`);
        pill.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');

        const fill = document.createElement('span');
        fill.className = 'bioluma-core__pill-fill';
        fill.setAttribute('aria-hidden', 'true');
        pill.appendChild(fill);

        pill.addEventListener('click', () => this.scrollToSlide(index));
        this.pillsWrap.appendChild(pill);
        this.pills.push(pill);
      });

      this.updateActivePill();
    }

    getActiveIndex() {
      const scrollLeft = this.track.scrollLeft;
      let closest = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          closest = index;
        }
      });

      return closest;
    }

    updateActivePill() {
      if (!this.pills.length) return;

      this.activeIndex = this.getActiveIndex();

      this.pills.forEach((pill, index) => {
        const active = index === this.activeIndex;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    scrollToSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  if (!customElements.get('bioluma-core-carousel')) {
    customElements.define('bioluma-core-carousel', BiolumaCoreCarousel);
  }
})();
